<template>
  <b-modal
    id="change-origin-modal"
    ref="modal"
    header-class="headerModal"
    centered
    :title="`${$t('ChangeOrigin')} - ` + (variant?.materialVariantName || '')"
    hide-footer
    @close="onCancel"
    @shown="onModalShown"
  >
    <b-form>
      <b-form-group :label="`${$t('SelectMaterial')}:`">
        <vue-select
          ref="vSelect"
          v-model="selectedMaterial"
          :options="materialsOptions"
          label="name"
          :reduce="m => m.materialId"
          :placeholder="$t('SelectMaterialPlaceholder')"
          :filterable="false"
          @search="onSearchMaterial"
          @open="onOpen"
          @close="onClose"
          :max-height="200"
        >
          <template #option="{ name }">
            <div>{{ name }}</div>
          </template>
          <template #selected-option="{ name }">
            <div>{{ name }}</div>
          </template>
          <template #list-footer>
            <div v-if="loading" class="text-center p-2">
              <div class="spinner-border spinner-border-sm" />
              {{ $t('LoadingMore') }}
            </div>
            <div v-else-if="!hasMore && !isSearching" class="text-center p-2 text-muted">
              {{ $t('NoMoreMaterials') }}
            </div>
            <li v-else-if="hasMore && !isSearching" style="height: 1px;"></li> <!-- Invisible spacer to ensure scrollable area -->
          </template>
        </vue-select>
      </b-form-group>
    </b-form>

    <div class="buttonsEverywhere">
      <b-button
        type="button"
        :disabled="loading || !selectedMaterial"
        class="buttonSubmit"
        @click="onSubmit"
      >
        <div v-if="loading" class="spinner-border spinner-border-sm" />
        {{ $t('Submit') }}
      </b-button>

      <b-button
        type="button"
        variant="danger"
        class="buttonCancel"
        style="margin-right: 15px; background: white; color: black; border-color: white; font-weight: 400;"
        @click="onCancel"
      >
        {{ $t('Cancel') }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  props: {
    variant: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      selectedMaterial: null,
      page: 1,
      pageSize: 15,
      searchText: '',
      loading: false,
      materials: [],
      searchedMaterials: [],
      debounceTimer: null,
      scrollListener: null,
      hasNextPage: true,
    };
  },

  computed: {
    isSearching() {
      return this.searchText.length >= 2;
    },
    materialsOptions() {
      return this.isSearching ? this.searchedMaterials : this.materials;
    },
    hasMore() {
      return this.hasNextPage;
    },
  },

  methods: {
    ...mapActions(['loadMaterials', 'filteredMaterialsByName', 'editMaterialVariantOrigin']),

    onModalShown() {
      if (!this.materials.length && !this.isSearching) {
        this.fetchMaterials(true);
      }
    },

    async fetchMaterials(reset = false) {
      if (reset) {
        this.page = 1;
        this.materials = [];
        this.hasNextPage = true;
      }

      if (this.loading || !this.hasNextPage) return;
      this.loading = true;

      try {
        await this.loadMaterials({
          pageNumber: this.page,
          pageSize: this.pageSize,
          materialName: '',
          measurementType: 'ALL',
        });

        const latestPage = this.$store.getters.getMaterials || [];

        this.materials.push(...latestPage);

        if (latestPage.length < this.pageSize) {
          this.hasNextPage = false;
        }
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        this.loading = false;
      }
    },

    async fetchSearchedMaterials() {
      this.loading = true;
      try {
        await this.filteredMaterialsByName({
          materialName: this.searchText,
          measurementType: null,
        });

        this.searchedMaterials = this.$store.getters.getFilteredMaterials || [];
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        this.loading = false;
      }
    },

    onSearchMaterial(search) {
      this.searchText = search?.trim() || '';

      if (this.debounceTimer) clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        if (this.isSearching) {
          this.searchedMaterials = [];
          this.fetchSearchedMaterials();
        } else {
          this.searchedMaterials = [];
          this.fetchMaterials(true);
        }
      }, 350);
    },

    loadMore() {
      if (!this.hasMore || this.loading || this.isSearching) return;

      this.page++;
      this.fetchMaterials();
    },

    handleScroll(e) {
      if (this.isSearching) return;

      const el = e.target;
      const offset = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (offset < 100) {
        this.loadMore();
      }
    },

    onOpen() {
      this.$nextTick(() => {
        const dropdown = this.$refs.vSelect.$el.querySelector('.vs__dropdown-menu');
        if (dropdown) {
          this.scrollListener = this.handleScroll;
          dropdown.addEventListener('scroll', this.scrollListener);
        }
      });
    },

    onClose() {
      const dropdown = this.$refs.vSelect.$el.querySelector('.vs__dropdown-menu');
      if (dropdown && this.scrollListener) {
        dropdown.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
      }
    },

    async onSubmit() {
      if (!this.selectedMaterial) return;

      this.loading = true;
      try {
        await this.editMaterialVariantOrigin({
          materialVariantId: this.variant.materialVariantId,
          newOriginMaterialId: this.selectedMaterial,
          successCallback: () => {
            this.$emit('originChanged');
            this.$refs.modal.hide();
          },
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    onCancel() {
      this.$refs.modal.hide();
      this.selectedMaterial = null;
      this.page = 1;
      this.materials = [];
      this.searchedMaterials = [];
      this.hasNextPage = true;
      this.searchText = '';
      this.loading = false;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
    },
  },

  beforeDestroy() {
    this.onClose();
  },
};
</script>

<style scoped>
form {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
</style>